import { useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useState } from 'react'
import StructuredData from '../components/StructuredData'
import './FumeHoodsLandingPage.css'

interface QuoteFormData {
  name: string
  email: string
  phone: string
  company?: string
  referral?: string
  projectType: string
}

function FumeHoodsLandingPage() {
  const formRef = useRef<HTMLDivElement>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitError, setIsSubmitError] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<QuoteFormData>()

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mojnjjob'

  const onSubmit = async (data: QuoteFormData) => {
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company || '',
          referral: data.referral || '',
          projectType: data.projectType,
          _subject: 'Lab Fume Hoods Quote Request',
        }),
      })
      if (!res.ok) throw new Error('Submit failed')
      setIsSubmitted(true)
      reset()
      setTimeout(() => setIsSubmitted(false), 8000)
    } catch {
      setIsSubmitError(true)
      setTimeout(() => setIsSubmitError(false), 5000)
    }
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Laboratory Fume Hoods',
    description:
      'Custom, ducted, and ductless fume hoods designed to fit your laboratory equipment and meet safety requirements. Down draft tables, slot exhausters, and snorkel systems available.',
    brand: { '@type': 'Brand', name: 'Laboratory Specialists' },
    offers: { '@type': 'Offer', priceCurrency: 'USD', availability: 'https://schema.org/UponRequest' },
  }

  return (
    <div className="fhood-landing-page">
      <StructuredData data={structuredData} />

      {/* Hero: headline + sub-headline + proof + CTA */}
      <section className="fhood-hero">
        <div className="fhood-hero-bg" aria-hidden />
        <div className="container fhood-hero-inner">
          <h1 className="fhood-hero-title">
            Fume Hoods That Protect Your Lab and Your People
          </h1>
          <p className="fhood-hero-subtitle">
            Ducted and ductless fume hoods, down draft tables, slot exhausters, and snorkel systems. We design and install exhaust solutions that fit your equipment and meet safety standards.
          </p>
          <div className="fhood-hero-cta">
            <button type="button" className="fhood-cta-button" onClick={scrollToForm}>
              Get Your Free Quote
            </button>
            <p className="fhood-hero-cta-note">Custom quote in 24 hours · No obligation</p>
          </div>
        </div>
      </section>

      {/* Three objections (below the fold) */}
      <section className="fhood-objections">
        <div className="container">
          <ul className="fhood-bullets">
            <li>
              <strong>Not sure which type fits your application?</strong> We'll recommend ducted or ductless fume hoods, down draft tables, slot exhausters, or snorkel systems based on your chemicals and workspace.
            </li>
            <li>
              <strong>Worried about fitting around existing equipment?</strong> We design custom hoods that properly accommodate your lab equipment and maximize workspace efficiency.
            </li>
            <li>
              <strong>Need one partner for the whole job?</strong> We handle design, fabrication, installation, and compliance testing—one quote, one timeline.
            </li>
          </ul>
        </div>
      </section>

      {/* Fume hood testing & maintenance */}
      <section className="fhood-testing">
        <div className="container">
          <h2 className="fhood-testing-title">Fume Hood Testing & Replacement Guidance</h2>
          <p className="fhood-testing-intro">
            A fume hood that looks fine may not be safe. Regular testing and timely part replacement are critical to protecting your team.
          </p>

          <div className="fhood-testing-grid">
            <div className="fhood-testing-card">
              <h3>Material Degradation</h3>
              <p>
                Chemical exposure causes corrosion of internal parts, discolored surfaces, and fogged sash windows that cannot be cleaned. These are signs the hood needs replacement—not just cleaning.
              </p>
            </div>

            <div className="fhood-testing-card">
              <h3>Replacement Cycles</h3>
              <ul>
                <li><strong>Exhaust fan belts</strong> — every 1–2 years</li>
                <li><strong>Filters (ductless hoods)</strong> — every 6–24 months depending on chemical load</li>
              </ul>
            </div>

            <div className="fhood-testing-card">
              <h3>Technological Obsolescence</h3>
              <p>
                Fume hoods manufactured before 1985 may not meet current ASHRAE 110 containment standards. An older hood may appear operational while providing inadequate protection by modern benchmarks.
              </p>
            </div>
          </div>

          <p className="fhood-testing-cta-note">
            Not sure if your hoods are up to standard?{' '}
            <button type="button" className="fhood-testing-link" onClick={scrollToForm}>
              Request a free assessment.
            </button>
          </p>
        </div>
      </section>

      {/* Quote form - primary conversion */}
      <section className="fhood-form-section" ref={formRef} id="quote-form">
        <div className="container">
          <div className="fhood-form-card">
            <h2 className="fhood-form-title">Get Your Custom Quote</h2>
            <p className="fhood-form-lead-magnet">
              Get a custom quote for ducted or ductless fume hoods and exhaust systems—no obligation.
            </p>

            {isSubmitted ? (
              <div className="fhood-form-success">
                <h3>Thanks—we'll be in touch within 24 hours.</h3>
                <p>We'll email or call with your quote and next steps.</p>
              </div>
            ) : (
              <form className="fhood-form" onSubmit={handleSubmit(onSubmit)}>
                {isSubmitError && (
                  <p className="fhood-form-error" role="alert">
                    Something went wrong. Please try again or call us at (541) 658-9374.
                  </p>
                )}
                <div className="fhood-form-group">
                  <label htmlFor="fhood-name">
                    Full Name <span className="fhood-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="fhood-name"
                    {...register('name', {
                      required: 'Name is required',
                      minLength: { value: 2, message: 'Please enter your full name' },
                    })}
                    aria-invalid={errors.name ? 'true' : 'false'}
                    placeholder="First and Last Name"
                  />
                  {errors.name && (
                    <span className="fhood-error" role="alert">
                      {errors.name.message}
                    </span>
                  )}
                </div>

                <div className="fhood-form-group">
                  <label htmlFor="fhood-email">
                    Email <span className="fhood-required">*</span>
                  </label>
                  <input
                    type="email"
                    id="fhood-email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email',
                      },
                    })}
                    aria-invalid={errors.email ? 'true' : 'false'}
                    placeholder="Email Address"
                  />
                  {errors.email && (
                    <span className="fhood-error" role="alert">
                      {errors.email.message}
                    </span>
                  )}
                </div>

                <div className="fhood-form-group">
                  <label htmlFor="fhood-phone">
                    Phone <span className="fhood-required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="fhood-phone"
                    {...register('phone', {
                      required: 'Phone is required',
                      pattern: {
                        value: /^[\d\s\-+()]+$/,
                        message: 'Please enter a valid phone number',
                      },
                    })}
                    aria-invalid={errors.phone ? 'true' : 'false'}
                    placeholder="123-456-7890"
                  />
                  {errors.phone && (
                    <span className="fhood-error" role="alert">
                      {errors.phone.message}
                    </span>
                  )}
                </div>

                <div className="fhood-form-group">
                  <label htmlFor="fhood-company">Company / Institution (optional)</label>
                  <input
                    type="text"
                    id="fhood-company"
                    {...register('company')}
                    placeholder="Your lab or company name"
                  />
                </div>

                <div className="fhood-form-group">
                  <label htmlFor="fhood-referral">How did you hear about us? (optional)</label>
                  <input
                    type="text"
                    id="fhood-referral"
                    {...register('referral')}
                    placeholder="e.g. Google, referral, trade show..."
                  />
                </div>

                <div className="fhood-form-group">
                  <label htmlFor="fhood-projectType">
                    What do you need? <span className="fhood-required">*</span>
                  </label>
                  <select
                    id="fhood-projectType"
                    {...register('projectType', { required: 'Please select an option' })}
                    aria-invalid={errors.projectType ? 'true' : 'false'}
                  >
                    <option value="">Select...</option>
                    <option value="ducted-fume-hood">Ducted fume hood</option>
                    <option value="ductless-fume-hood">Ductless fume hood</option>
                    <option value="down-draft-table">Down draft table</option>
                    <option value="slot-exhauster">Slot exhauster</option>
                    <option value="snorkel-system">Snorkel / spot exhauster</option>
                    <option value="replacement">Replacement / retrofit</option>
                    <option value="not-sure">Not sure yet—need guidance</option>
                  </select>
                  {errors.projectType && (
                    <span className="fhood-error" role="alert">
                      {errors.projectType.message}
                    </span>
                  )}
                </div>

                <p className="fhood-privacy">
                  By submitting you agree to our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                  . We won't share your information.
                </p>

                <button type="submit" className="fhood-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Get My Quote'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="fhood-legal">
        <div className="container">
          <p className="fhood-legal-text">
            Laboratory Specialists. Lab equipment installation and fume hood systems. All installations comply with OSHA and ANSI standards.{' '}
            <a href="/privacy">Privacy Policy</a> · <a href="/contact">Contact</a>
          </p>
        </div>
      </section>
    </div>
  )
}

export default FumeHoodsLandingPage
